import { useState, useEffect, useRef, useCallback } from 'react';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from './ui/command';
import { cn } from '../lib/utils';

/**
 * AsyncCustomerPicker — type-to-search customer selector backed by
 * GET /api/customers/paginated (server-side search). Never loads every customer.
 *
 * Props:
 *  - value:        selected customer_id
 *  - onChange:     (id, customer) => void
 *  - selectedName: display name for the current value (edit mode), optional
 *  - placeholder / disabled / testId
 */
export function AsyncCustomerPicker({ value, onChange, selectedName, placeholder = 'Select customer', disabled = false, testId = 'async-customer-picker' }) {
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState(selectedName || '');
  const reqIdRef = useRef(0);
  const debTimer = useRef(null);

  // Keep the displayed label in sync with the selected value.
  useEffect(() => { if (selectedName) setLabel(selectedName); }, [selectedName]);

  // If we have a value but no label yet, resolve the single customer's name.
  useEffect(() => {
    let cancelled = false;
    if (value && !label) {
      api.get(`/customers/${value}`)
        .then((res) => { if (!cancelled) setLabel(res.data?.company_name || res.data?.contact_name || ''); })
        .catch(() => {});
    }
    if (!value) setLabel('');
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const fetchResults = useCallback(async (term) => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    try {
      const res = await api.get('/customers/paginated', {
        params: { search: term || undefined, page: 1, page_size: 20, status: 'active' },
      });
      if (myReq !== reqIdRef.current) return;
      setResults(res.data?.items || []);
    } catch (e) {
      if (myReq === reqIdRef.current) setResults([]);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [api]);

  // Load first page when opened; debounce subsequent typing.
  useEffect(() => {
    if (!open) return;
    if (debTimer.current) clearTimeout(debTimer.current);
    debTimer.current = setTimeout(() => fetchResults(search), search ? 300 : 0);
    return () => { if (debTimer.current) clearTimeout(debTimer.current); };
  }, [open, search, fetchResults]);

  const handleSelect = (customer) => {
    setLabel(customer.company_name || customer.contact_name || '');
    onChange?.(customer.id, customer);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testId}
          className={cn('w-full justify-between font-normal', !label && 'text-muted-foreground')}
        >
          <span className="truncate">{label || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        {/* shouldFilter=false: results come pre-filtered from the server */}
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search customers..."
            value={search}
            onValueChange={setSearch}
            data-testid={`${testId}-search`}
          />
          <CommandList>
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Searching...
              </div>
            ) : results.length === 0 ? (
              <CommandEmpty>{search ? 'No customers match your search.' : 'Type to search customers.'}</CommandEmpty>
            ) : (
              <CommandGroup>
                {results.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={c.id}
                    onSelect={() => handleSelect(c)}
                    data-testid={`${testId}-option-${c.id}`}
                  >
                    <Check className={cn('mr-2 h-4 w-4', value === c.id ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{c.company_name || c.contact_name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default AsyncCustomerPicker;
